/**
 * Marca SignCheck — prancha 1C do Design System (docs/design/signcheck-ds/).
 *
 * O "S" é um traço único de assinatura fechado por uma linha de base: o gesto e
 * a régua no mesmo sinal, dentro de um quadrado de aço escuro sem cantos
 * arredondados. Grade de construção de 48 unidades, traço 3.4, base em y=39.
 *
 * Regra de construção do próprio Design System: a partir de 20px para baixo a
 * linha de base some e o traço engrossa — em miniatura a base vira sujeira e o
 * "S" fecha visualmente.
 */
import { ptBR } from '@/i18n/pt-BR';

const TRACO_S =
  'M33 15c-2.6-3.4-9.6-4.4-12.6-1.1-2.8 3.1.6 6.8 5.2 8.6 5 2 8.6 3.9 8.6 8.4 0 4.6-5.6 6.6-10 5.5-3.6-.9-6-3.3-6.8-5.7';
const LINHA_DE_BASE = 'M9 39c7-3.4 17-6.2 30-4.6';

/** Abaixo deste tamanho a marca usa a variante sem linha de base. */
const LIMITE_COMPACTO = 20;

export function BrandMark({ size = 26 }: { size?: number }) {
  const compacta = size <= LIMITE_COMPACTO;
  return (
    <svg
      viewBox="0 0 48 48"
      width={size}
      height={size}
      className="shrink-0"
      aria-hidden="true"
      focusable="false"
      data-testid="brand-mark"
      data-variant={compacta ? 'compacta' : 'completa'}
    >
      <rect x="1" y="1" width="46" height="46" fill="oklch(var(--accent-900))" />
      <path
        d={TRACO_S}
        fill="none"
        stroke="oklch(var(--bg))"
        strokeWidth={compacta ? 3.8 : 3.4}
        strokeLinecap="round"
      />
      {compacta ? null : (
        <path
          d={LINHA_DE_BASE}
          fill="none"
          stroke="oklch(var(--accent-400))"
          strokeWidth={1.6}
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

/**
 * Wordmark: "SignCheck" em Barlow Condensed 600 (`font-marca`, E165) com tracking -0.02em, sobre um
 * eyebrow mono em caixa alta. O nome comercial é marca — não é o nome de
 * produto no texto corrido (`ptBR.productName`), conforme
 * docs/domain/naming-conventions.md.
 */
export function BrandWordmark({ eyebrow, size = 'md' }: { eyebrow: string; size?: 'md' | 'lg' }) {
  return (
    <div className="flex flex-col leading-tight" data-testid="brand-wordmark">
      <span
        className={
          size === 'lg'
            ? 'font-marca text-heading-lg font-semibold tracking-[-0.02em] text-fg'
            : 'font-marca text-[17px] font-semibold tracking-[-0.02em] text-fg'
        }
      >
        {ptBR.shell.brand}
      </span>
      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-fg-muted">
        {eyebrow}
      </span>
    </div>
  );
}
